import {
  SAMLAuthProvider,
  type User,
  getAdditionalUserInfo,
  getRedirectResult,
  onAuthStateChanged,
  signInWithRedirect,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { type ReactNode, useEffect, useState } from 'react';

import { AuthContext, type AuthContextType } from './AuthContext.ts';
import { auth } from './firebase.ts';

interface SAMLAttributes {
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const signInWithSAML = async () => {
    const provider = new SAMLAuthProvider('saml.watt-utilities-google-workspace');
    await signInWithRedirect(auth, provider);
  };

  const logout = async () => {
    await signOut(auth);
  };

  const updateUserDisplayName = async (user: User, samlAttributes: SAMLAttributes) => {
    try {
      // Set displayName if we have name information and it's not already set
      if ((samlAttributes.firstName || samlAttributes.lastName) && !user.displayName) {
        const displayName = [samlAttributes.firstName, samlAttributes.lastName].filter(Boolean).join(' ').trim();

        if (displayName) {
          await updateProfile(user, { displayName });
          console.log('Updated user displayName:', displayName);
        }
      }
    } catch (error) {
      console.error('Failed to update user profile:', error);
    }
  };

  useEffect(() => {
    // Check for redirect result on mount
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result && result.user) {
          console.log('SAML sign-in successful', result.user);

          // Get additional user info containing SAML attributes
          const additionalUserInfo = getAdditionalUserInfo(result);
          if (additionalUserInfo?.profile) {
            const samlProfile = additionalUserInfo.profile as SAMLAttributes;
            await updateUserDisplayName(result.user, samlProfile);
          }
        }
      } catch (error) {
        console.error('SAML redirect error:', error);
        // Store error for display on login page if needed
        sessionStorage.setItem('saml_error', error instanceof Error ? error.message : 'Authentication failed');
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    // Check redirect result after setting up the auth state listener
    checkRedirectResult();

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    loading,
    signInWithSAML,
    logout,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
}
